import React from "react";
import {
  Button,
  Box,
  Typography,
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Divider,
  ListItemIcon,
} from "@mui/material";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import LoginOutlined from "@mui/icons-material/LoginOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import LocalHospitalOutlined from "@mui/icons-material/LocalHospitalOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ForwardToInboxOutlined from "@mui/icons-material/ForwardToInboxOutlined";
import { Link as RouterLink } from "react-router-dom";
import FlexBetween from "components/global/FlexBetween";
import ProfileAvatar from "components/user/ProfileAvatar";

const NavbarUserMenu = ({
  chatRef,
  handleClick,
  isOpen,
  handleClose,
  isLoggedIn,
  currentUser,
  logOut,
  theme,
}) => {
  const handleItemClick = () => handleClose();

  return (
    <FlexBetween>
      <Button
        ref={chatRef}
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : "false"}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textTransform: "none",
          gap: "0.75rem",
          px: 1.25,
          py: 0.75,
          borderRadius: "10px",
          transition: "background-color 160ms ease, transform 160ms ease",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {isLoggedIn ? (
          <>
            <ProfileAvatar user={currentUser.user} size="25" />
            <Box textAlign="left">
              <Typography
                fontWeight={600}
                fontSize="0.95rem"
                letterSpacing="0.01em"
                sx={{
                  color: theme.palette.primary.main,
                  lineHeight: "25px",
                }}
              >
                {currentUser.user.username}
              </Typography>
            </Box>
          </>
        ) : (
          <PersonOutlined sx={{ color: theme.palette.primary.main }} />
        )}

        <ArrowDropDownOutlined
          sx={{
            color: theme.palette.primary[300],
            fontSize: "22px",
            transition: "transform 200ms ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Button>

      <Popper
        open={isOpen}
        anchorEl={chatRef.current}
        placement="bottom-end"
        transition
        disablePortal={false}
        modifiers={[
          { name: "offset", options: { offset: [0, 10] } },
          {
            name: "preventOverflow",
            options: { boundary: "viewport", padding: 8 },
          },
          { name: "flip", enabled: false },
        ]}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top right" }} timeout={180}>
            <Paper
              elevation={0}
              sx={{
                minWidth: 220,
                overflow: "visible",
                mt: 0.5,
                borderRadius: "14px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                boxShadow:
                  "0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.10)",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: -5,
                  right: 18,
                  width: 10,
                  height: 10,
                  bgcolor: theme.palette.background.paper,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  transform: "rotate(45deg)",
                  zIndex: 0,
                },
                "& .MuiMenuItem-root": {
                  fontSize: "0.95rem",
                  py: 1,
                  px: 1.5,
                  mx: 0.5,
                  my: 0.25,
                  borderRadius: "8px",
                  transition: "background-color 120ms ease",
                },
                "& .MuiListItemIcon-root": {
                  minWidth: 32,
                  color: theme.palette.text.secondary,
                },
                "& .MuiAvatar-root": {
                  width: 26,
                  height: 26,
                  mr: 1,
                },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={isOpen} disablePadding sx={{ py: 0.75 }}>
                  {isLoggedIn && [
                    <MenuItem
                      key="profile"
                      component={RouterLink}
                      to={"./profile"}
                      onClick={handleItemClick}
                    >
                      <ProfileAvatar user={currentUser.user} size="24" />
                      Profile
                    </MenuItem>,
                    <MenuItem
                      key="account"
                      component={RouterLink}
                      to={"./user/" + currentUser.user.username}
                      onClick={handleItemClick}
                    >
                      <ListItemIcon>
                        <ManageAccountsOutlined fontSize="small" />
                      </ListItemIcon>
                      My Account
                    </MenuItem>,
                    <Divider key="d1" sx={{ my: 0.75, mx: 1 }} />,
                  ]}

                  <MenuItem
                    component={RouterLink}
                    to={"/contact/"}
                    onClick={handleItemClick}
                  >
                    <ListItemIcon>
                      <ForwardToInboxOutlined fontSize="small" />
                    </ListItemIcon>
                    Contact Us
                  </MenuItem>

                  <MenuItem
                    component={RouterLink}
                    to={"/status/"}
                    onClick={handleItemClick}
                  >
                    <ListItemIcon>
                      <LocalHospitalOutlined fontSize="small" />
                    </ListItemIcon>
                    Status of OpsBlox
                  </MenuItem>

                  <MenuItem
                    component={RouterLink}
                    to={"/about/"}
                    onClick={handleItemClick}
                  >
                    <ListItemIcon>
                      <InfoOutlined fontSize="small" />
                    </ListItemIcon>
                    About OpsBlox
                  </MenuItem>

                  <Divider sx={{ my: 0.75, mx: 1 }} />

                  {isLoggedIn ? (
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        logOut();
                      }}
                    >
                      <ListItemIcon>
                        <LogoutOutlined fontSize="small" />
                      </ListItemIcon>
                      Logout
                    </MenuItem>
                  ) : (
                    <MenuItem
                      component={RouterLink}
                      to="./login"
                      onClick={handleItemClick}
                    >
                      <ListItemIcon>
                        <LoginOutlined fontSize="small" />
                      </ListItemIcon>
                      Login
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </FlexBetween>
  );
};

export default NavbarUserMenu;
